// Security — SSN and the encryption passkey. Admin only.
//
// The last capability the classic Admin panel held on its own. Until this
// screen existed, Users and Crew both carried a link to /classic reading
// "SSNs, the encryption passkey and signing in as someone stay in the classic
// Admin panel" — which stops being true the moment the classic tree is
// deleted, and stops being ACCEPTABLE before that, because a payroll clerk
// who cannot record a new hire's SSN cannot run payroll.
//
// ── What this file is and is not ────────────────────────────────────────────
//
// It is a screen. It calls endpoints that already exist and changes NOTHING
// under server/utils/encryption.js, ssnEncryption.js, permissions.js,
// middleware/auth.js or routes/auth.js — CLAUDE.md rule 5 is untouched, and
// deliberately so: the cryptography, the passkey handling and the audit trail
// are the server's and stay the server's.
//
//   POST /workers/generate-passkey          → { passkey, warning, instructions }
//   POST /workers/:id/ssn { ssn, passkey }  → { message }
//   POST /workers/:id/ssn/reveal { passkey }→ { ssn } | 401 { error }
//   GET  /workers/ssn/migration-status      → { total, legacy, current, unknown, migration_needed }
//   POST /workers/ssn/migrate { passkey }   → { message, migrated, already_migrated, unknown_format, total }
//
// ── The passkey is never stored, anywhere ───────────────────────────────────
//
// Not in localStorage, not in a ref that outlives the sheet, not in a query
// cache. It is typed, sent, and the state holding it is cleared. That is the
// whole security model: the server holds ciphertext it cannot read without a
// key nobody keeps. A "remember this passkey" convenience would quietly undo
// it, so the field is `type="password"` with autoComplete off and the state
// is wiped on every close.

import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { post, serverSentence } from '../../shared/http';
// The ONE role vocabulary (apps/shared/roles.js). This screen printed the raw
// key — "worker" — beside a name, on the screen that handles SSNs.
import { roleLabel } from '../../shared/roles';
import { useRole } from '../../../hooks/usePermission';
import {
  DataTable, Pill, FwButton, FwField, FwInput, Notice, EmptyState, Skeleton,
} from '../../../ui/fw';
import {
  useOfficeQuery, useAction, PageHeader, Toolbar, SearchBox, Panel, Split,
  FormError, FormRow, matches, textOr,
} from '../records/OfficePage';
import './admin.css';

/** XXX-XX-XXXX, which is exactly what server/routes/workers.js validates. */
export const SSN_SHAPE = /^\d{3}-\d{2}-\d{4}$/;

/**
 * The one sentence wrong with an SSN entry, or null.
 *
 * Both halves are checked here so a refusal names the real reason rather than
 * surfacing a validator array — but the server checks the same two, and it is
 * the authority. This exists so somebody typing on a phone is told before the
 * round trip, not instead of it.
 */
export function ssnProblem({ ssn, passkey }) {
  if (!SSN_SHAPE.test(String(ssn || '').trim())) return 'An SSN looks like 123-45-6789.';
  if (!String(passkey || '').trim()) return 'The passkey is needed to encrypt it.';
  return null;
}

/** Type as you go: 123456789 becomes 123-45-6789. Anything else is left alone. */
export function formatSsn(value) {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 9);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}

/**
 * Whether a worker row has an SSN on file.
 *
 * `ssn_hash` is what the list carries, and the server STRIPS it for anyone
 * without workers.read_sensitive — so an absent field means "not allowed to
 * know", which is not the same as "no SSN". This screen is admin-only, so the
 * field is present; the distinction is kept in the shape of the answer
 * (`null` vs `false`) rather than flattened, because a screen that says "no
 * SSN on file" when it simply cannot see is how somebody enters one twice.
 */
export function hasSsn(worker) {
  if (!worker || !('ssn_hash' in worker)) return null;
  return !!worker.ssn_hash;
}

function PasskeySheetField({ id, value, onChange, hint }) {
  return (
    <FwField label="Encryption passkey" htmlFor={id} hint={hint} required>
      <FwInput
        id={id}
        type="password"
        value={value}
        autoComplete="off"
        onChange={(e) => onChange(e.target.value)}
        placeholder="Paste it from your password manager"
      />
    </FwField>
  );
}

/** Generate a passkey. Shown once; the server never stores it. */
function PasskeyPanel() {
  const [made, setMade] = useState(null);
  const [copied, setCopied] = useState(false);
  const { busy, error, run } = useAction(serverSentence);

  const generate = () => run(async () => {
    const data = await post('/workers/generate-passkey');
    setMade(data);
    setCopied(false);
  }, 'Could not generate a passkey.');

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(made.passkey);
      setCopied(true);
    } catch (_) {
      // No clipboard permission. The key is on screen and selectable; saying
      // "copied" when it was not is worse than saying nothing.
      setCopied(false);
    }
  };

  return (
    <Panel title="Encryption passkey">
      <FormError error={error} />
      <p className="or-muted">
        SSNs are encrypted with this passkey. The server stores the encrypted
        value and never the key, so it cannot be recovered — if it is lost,
        every SSN on file becomes unreadable and has to be entered again.
      </p>
      {made ? (
        <>
          <Notice tone="warn" title="Shown once">
            {made.warning || 'Store this in your password manager now. It cannot be shown again.'}
          </Notice>
          <pre className="or-log" data-testid="new-passkey">{made.passkey}</pre>
          <div className="or-actions">
            <FwButton variant="ghost" icon="copy" onClick={copy}>Copy</FwButton>
            {copied ? <span className="or-muted">Copied.</span> : null}
            <FwButton variant="quiet" onClick={() => { setMade(null); setCopied(false); }}>
              Done — I have saved it
            </FwButton>
          </div>
        </>
      ) : (
        <div className="or-actions">
          <FwButton onClick={generate} loading={busy} icon="lock">Generate a new passkey</FwButton>
        </div>
      )}
    </Panel>
  );
}

/** Re-encrypt legacy SSN rows onto the per-record-salt format. */
function MigrationPanel() {
  const q = useOfficeQuery(['security', 'ssn-migration'], '/workers/ssn/migration-status');
  const [passkey, setPasskey] = useState('');
  const [result, setResult] = useState(null);
  const { busy, error, run } = useAction(serverSentence);

  const status = q.data;
  const migrate = () => run(async () => {
    const data = await post('/workers/ssn/migrate', { passkey });
    setResult(data);
    setPasskey('');
    await q.refetch();
  }, 'Migration failed.');

  return (
    <Panel title="SSN encryption format">
      <FormError error={error} />
      {q.isLoading ? <Skeleton lines={2} /> : null}
      {status ? (
        <>
          <p className="or-muted">
            {status.total === 0
              ? 'No SSNs are on file, so there is nothing to migrate.'
              : `${status.current} of ${status.total} on the current format.`}
            {status.unknown ? ` ${status.unknown} could not be read at all.` : ''}
          </p>
          {status.migration_needed ? (
            <>
              <Notice tone="warn" title={`${status.legacy} need re-encrypting`}>
                These were encrypted before per-record salt. Re-encrypting is safe
                to run twice — rows already on the new format are skipped.
              </Notice>
              <FormRow>
                <PasskeySheetField
                  id="or-migrate-passkey"
                  value={passkey}
                  onChange={setPasskey}
                  hint="The same passkey the existing SSNs were encrypted with."
                />
              </FormRow>
              <div className="or-actions">
                <FwButton onClick={migrate} loading={busy} disabled={!passkey.trim()}>
                  Re-encrypt {status.legacy}
                </FwButton>
              </div>
            </>
          ) : null}
          {result ? (
            <Notice tone="ok" onDismiss={() => setResult(null)}>
              {result.message} {result.migrated} re-encrypted, {result.already_migrated} already current.
            </Notice>
          ) : null}
        </>
      ) : null}
    </Panel>
  );
}

/** One worker: record an SSN, or reveal the one on file. */
function WorkerSsn({ worker, onSaved }) {
  const [mode, setMode] = useState(null);         // 'set' | 'reveal'
  const [ssn, setSsn] = useState('');
  const [passkey, setPasskey] = useState('');
  const [revealed, setRevealed] = useState(null);
  const [saved, setSaved] = useState(null);
  const { busy, error, run } = useAction(serverSentence);

  // Nothing typed here outlives the panel. Switching worker, or closing the
  // form, wipes both the passkey and any revealed number.
  const reset = () => { setSsn(''); setPasskey(''); setRevealed(null); setMode(null); };

  const save = () => {
    const problem = ssnProblem({ ssn, passkey });
    if (problem) { setSaved({ error: problem }); return; }
    setSaved(null);
    return run(async () => {
      const data = await post(`/workers/${worker.id}/ssn`, { ssn: ssn.trim(), passkey });
      // `data.last_four` is deliberately not read. The route used to send four
      // characters of sha256(ssn) under that name and this screen printed
      // "Ends 3b2f" — a digest fragment labelled as the digits just typed,
      // on the one screen where a plausible wrong number is the whole
      // failure. The route no longer sends it (server/routes/workers.js);
      // ignoring it here is what makes a droplet running an older server
      // harmless rather than merely unlikely.
      setSaved({ ok: data.message || 'Saved.' });
      reset();
      if (onSaved) onSaved();
    }, 'Could not save that SSN.');
  };

  const reveal = () => run(async () => {
    const data = await post(`/workers/${worker.id}/ssn/reveal`, { passkey });
    setRevealed(data.ssn);
    setPasskey('');
  }, 'Could not read that SSN.');

  const on = hasSsn(worker);

  return (
    <Panel title={worker.name}>
      <FormError error={error} />
      {saved && saved.error ? <Notice tone="stop" onDismiss={() => setSaved(null)}>{saved.error}</Notice> : null}
      {saved && saved.ok ? (
        <Notice tone="ok" onDismiss={() => setSaved(null)}>
          {saved.ok} Nothing here shows the number back — use Reveal it, with the
          passkey, to check the digits.
        </Notice>
      ) : null}

      <p className="or-muted">
        {on === null
          ? 'Whether an SSN is on file is not visible to you.'
          : on ? 'An SSN is on file.' : 'No SSN on file.'}
      </p>

      <div className="or-actions">
        <FwButton variant={mode === 'set' ? 'primary' : 'ghost'} onClick={() => { reset(); setMode('set'); }}>
          {on ? 'Replace the SSN' : 'Record an SSN'}
        </FwButton>
        {on ? (
          <FwButton variant={mode === 'reveal' ? 'primary' : 'ghost'} onClick={() => { reset(); setMode('reveal'); }}>
            Reveal it
          </FwButton>
        ) : null}
        {mode ? <FwButton variant="quiet" onClick={reset}>Cancel</FwButton> : null}
      </div>

      {mode === 'set' ? (
        <>
          <FormRow>
            <FwField label="Social security number" htmlFor="or-ssn" required>
              <FwInput
                id="or-ssn"
                value={ssn}
                inputMode="numeric"
                autoComplete="off"
                placeholder="123-45-6789"
                onChange={(e) => setSsn(formatSsn(e.target.value))}
              />
            </FwField>
            <PasskeySheetField
              id="or-ssn-passkey"
              value={passkey}
              onChange={setPasskey}
              hint="Not stored. Every SSN must use the SAME passkey or the old ones stop opening."
            />
          </FormRow>
          <div className="or-actions">
            <FwButton onClick={save} loading={busy} disabled={!ssn.trim() || !passkey.trim()}>Save</FwButton>
          </div>
        </>
      ) : null}

      {mode === 'reveal' ? (
        <>
          <Notice tone="warn">
            Every reveal is written to the access log with your name.
          </Notice>
          <FormRow>
            <PasskeySheetField id="or-reveal-passkey" value={passkey} onChange={setPasskey} />
          </FormRow>
          <div className="or-actions">
            <FwButton onClick={reveal} loading={busy} disabled={!passkey.trim()}>Reveal</FwButton>
          </div>
          {revealed ? (
            <>
              <pre className="or-log" data-testid="revealed-ssn">{revealed}</pre>
              <FwButton variant="quiet" onClick={() => setRevealed(null)}>Hide it</FwButton>
            </>
          ) : null}
        </>
      ) : null}
    </Panel>
  );
}

export default function Security() {
  const role = useRole();
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);
  const workers = useOfficeQuery(['security', 'workers'], '/workers', { enabled: role === 'admin' });

  if (role !== 'admin') {
    return (
      <div className="or-page ad-page" data-screen="office-Security">
        <PageHeader title="Security" />
        <EmptyState icon="lock" title="Admins only">
          Reading or recording an SSN is limited to administrators, and every
          reveal is logged.
        </EmptyState>
      </div>
    );
  }

  const rows = (Array.isArray(workers.data) ? workers.data : [])
    .filter((w) => matches(w, ['name', 'email', 'role'], q));
  const current = selected && rows.find((w) => w.id === selected.id);

  return (
    <div className="or-page ad-page" data-screen="office-Security">
      <PageHeader title="Security" meta="SSNs and the encryption passkey. Every reveal is logged." />

      {/*
        THE LIST FIRST. Measured at 390x780 on 2026-09-10: the passkey panel
        was y=127 h=300, the migration panel y=447 h=143, and the first SSN row
        landed at y=772 of a 780px screen — the screen opened on nothing.

        443px of that was two tools: a passkey generator used once ever, and a
        migration panel that on a healthy database says only "No SSNs are on
        file, so there is nothing to migrate." Same shape as the filter defect
        the 2026-09-10 audit fixed on Jobs and Activity — what you came to look
        at underneath what you might occasionally reach for.

        Reordered rather than put behind MoreFilters' phone-only disclosure:
        these are whole panels with warnings in them, "how many are on" means
        nothing for a tool, and a disclosure that hides a control is the
        DOM-present-but-untappable shape CLAUDE.md rule 6 records this repo
        shipping twice. Moving them costs no control at any width and hides
        nothing. Both keep every word, in the same order, directly below.
      */}
      <Panel title="Social security numbers">
        <Toolbar>
          <SearchBox value={q} onChange={setQ} placeholder="Search people" />
        </Toolbar>
        <Split
          main={(
            workers.isLoading ? <Skeleton lines={4} /> : (
            <DataTable
              rows={rows}
              rowKey={(w) => w.id}
              onRowClick={(w) => setSelected(w)}
              empty="Nobody matches that."
              caption="People"
              // The card, on a phone: the person's name, and whether an SSN
              // is ON FILE as the badge. That is the status this screen
              // exists to show, and it has three states — on file, none, and
              // "this list did not carry the answer", which the render below
              // keeps distinct. Role is the single fact.
              columns={[
                { key: 'name', label: 'Name', role: 'title', render: (w) => textOr(w.name) },
                { key: 'role', label: 'Role', role: 'fact', render: (w) => <Pill tone="neutral">{textOr(roleLabel(w.role))}</Pill> },
                {
                  key: 'ssn',
                  label: 'SSN',
                  role: 'badge',
                  render: (w) => {
                    const on = hasSsn(w);
                    if (on === null) return <span className="or-muted">—</span>;
                    return on ? <Pill tone="ok">On file</Pill> : <span className="or-muted">None</span>;
                  },
                },
              ]}
            />
            )
          )}
          aside={current
            ? <WorkerSsn worker={current} onSaved={() => qc.invalidateQueries({ queryKey: ['security', 'workers'] })} />
            : <p className="or-muted">Pick somebody to record or reveal their SSN.</p>}
        />
      </Panel>

      {/* The tools, unchanged and in their original order — see the note above
          the list for why they are below it now. */}
      <PasskeyPanel />
      <MigrationPanel />
    </div>
  );
}
